import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getUser, refreshMe, logout, getUserHistory, getUserBookings,
  getPackages, startCheckout, getUrgencyTickets, setPin,
} from "@/lib/userAuth";
import { getToken, api, API } from "@/lib/api";
import {
  Coins, LogOut, MessageSquare, CalendarCheck, Sparkles, User,
  Ticket, History, ShoppingBag, Clock, AlertCircle, Loader2, Gift, Key,
  FileText, Download, Trash2, Lock, FilePlus, Send, Megaphone, Bot, Bell, NotebookPen, Paperclip, X,
} from "lucide-react";
import FeedbackWidget from "@/components/FeedbackWidget";
import { PushToggle } from "@/components/PushToggle";
import { AgendaPanel } from "@/components/AgendaPanel";
import { MyWaitlist } from "@/components/MyWaitlist";
import { AttachmentThumb } from "@/components/AttachmentThumb";
import { readAttachment, ACCEPTED_ATTACHMENTS } from "@/lib/files";
import { syncPushIfGranted } from "@/lib/push";

export default function Profilo() {
  const nav = useNavigate();
  const [user, setUser] = useState(getUser());
  const [history, setHistory] = useState({ consumptions: [], purchases: [] });
  const [bookings, setBookings] = useState({ upcoming: [], past: [] });
  const [packages, setPackages] = useState([]);
  const [tickets, setTickets] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [tab, setTab] = useState("wallet");
  const [pinInput, setPinInput] = useState("");
  const [pinSaved, setPinSaved] = useState(false);
  const [pinSaving, setPinSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [news, setNews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", new_password2: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [docRequests, setDocRequests] = useState([]);
  const [newDocReq, setNewDocReq] = useState({ doc_type: "", description: "", notes: "" });
  const [reqSending, setReqSending] = useState(false);
  const [reqAttachment, setReqAttachment] = useState(null);

  const cancelBooking = async (b) => {
    if (!window.confirm(`Vuoi annullare l'appuntamento del ${b.slot_date} alle ${b.slot_time}? Il posto verrà liberato per chi è in lista d'attesa.`)) return;
    try {
      await api.post(`/user/bookings/${b.id}/cancel`);
      getUserBookings().then(setBookings).catch(() => {});
    } catch (e) { alert(e.response?.data?.detail || "Errore annullamento"); }
  };

  useEffect(() => {
    if (!getToken()) { nav("/accedi"); return; }
    refreshMe().then((u) => u && setUser(u));
    getUserHistory().then(setHistory).catch(() => {});
    getUserBookings().then(setBookings).catch(() => {});
    getPackages().then(setPackages).catch(() => {});
    getUrgencyTickets().then(setTickets).catch(() => {});
    api.get("/user/documents").then((r) => setDocs(r.data.documents || [])).catch(() => {});
    api.get("/user/document-requests").then((r) => setDocRequests(r.data.requests || [])).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.news || [])).catch(() => {});
    loadNotifications();
    syncPushIfGranted();
    // Ticket live polling ogni 30s
    const iv = setInterval(() => getUrgencyTickets().then(setTickets).catch(() => {}), 30000);
    return () => clearInterval(iv);
  }, []);

  const downloadUserDoc = async (docId, filename) => {
    try {
      const res = await fetch(`${API}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert("Errore download"); }
  };

  const handleDeleteAccount = async () => {
    const confirm1 = confirm("⚠️ Sei sicuro di voler eliminare definitivamente il tuo account?\n\nQuesta azione è irreversibile: perderai i Labor Point residui, gli appuntamenti futuri saranno annullati e tutti i tuoi dati saranno cancellati.");
    if (!confirm1) return;
    const typed = prompt("Per confermare digita ELIMINA (in maiuscolo):");
    if (typed !== "ELIMINA") { alert("Cancellazione annullata."); return; }
    setDeleting(true);
    try {
      await api.delete("/users/me");
      logout();
      alert("Account eliminato. Grazie per aver usato Labor AI.");
      nav("/");
    } catch (e) {
      alert(e.response?.data?.detail || "Errore durante la cancellazione. Riprova o contatta caflaborvitobitonto@gmail.com.");
      setDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    setPwMsg(null);
    if (!pwForm.old_password || !pwForm.new_password) { setPwMsg({ type: "err", text: "Compila entrambi i campi" }); return; }
    if (pwForm.new_password !== pwForm.new_password2) { setPwMsg({ type: "err", text: "Le due nuove password non coincidono" }); return; }
    if (pwForm.new_password.length < 8) { setPwMsg({ type: "err", text: "Minimo 8 caratteri" }); return; }
    if (!/[A-Z]/.test(pwForm.new_password)) { setPwMsg({ type: "err", text: "Serve almeno una lettera maiuscola" }); return; }
    if (!/[0-9]/.test(pwForm.new_password)) { setPwMsg({ type: "err", text: "Serve almeno un numero" }); return; }
    setPwSaving(true);
    try {
      await api.post("/users/change-password", { old_password: pwForm.old_password, new_password: pwForm.new_password });
      setPwForm({ old_password: "", new_password: "", new_password2: "" });
      setPwMsg({ type: "ok", text: "✓ Password aggiornata con successo" });
    } catch (e) {
      setPwMsg({ type: "err", text: e.response?.data?.detail || "Errore aggiornamento password" });
    } finally { setPwSaving(false); }
  };

  const submitDocRequest = async () => {
    if (!newDocReq.doc_type.trim() || !newDocReq.description.trim()) { alert("Compila tipo documento e descrizione"); return; }
    setReqSending(true);
    try {
      const payload = { ...newDocReq };
      if (reqAttachment) { payload.attachment_filename = reqAttachment.filename; payload.attachment_base64 = reqAttachment.base64; }
      await api.post("/user/document-requests", payload);
      setNewDocReq({ doc_type: "", description: "", notes: "" });
      setReqAttachment(null);
      const r = await api.get("/user/document-requests");
      setDocRequests(r.data.requests || []);
      alert("✓ Richiesta inviata! Vito o Marianna la prenderanno in carico appena possibile.");
    } catch (e) { alert(e.response?.data?.detail || "Errore invio richiesta"); }
    finally { setReqSending(false); }
  };

  const loadNotifications = async () => {
    try {
      const r = await api.get("/user/notifications");
      setNotifications(r.data.notifications || []);
      setUnreadNotif(r.data.unread || 0);
    } catch {}
  };

  const markNotifRead = async (notifId) => {
    try {
      await api.post(`/user/notifications/${notifId}/read`);
      loadNotifications();
    } catch {}
  };

  const markAllNotifRead = async () => {
    try {
      await api.post("/user/notifications/read-all");
      loadNotifications();
    } catch {}
  };

  const deleteNotif = async (notifId) => {
    try {
      await api.delete(`/user/notifications/${notifId}`);
      loadNotifications();
    } catch {}
  };

  const handleBuy = async (pkgId) => {
    setCheckoutLoading(pkgId);
    try {
      const { checkout_url } = await startCheckout(pkgId);
      window.location.href = checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Errore durante il checkout. Riprova.");
      setCheckoutLoading(null);
    }
  };

  const handleSavePin = async () => {
    if (!/^\d{4,6}$/.test(pinInput)) { alert("Il PIN deve essere di 4-6 cifre numeriche"); return; }
    setPinSaving(true);
    try {
      await setPin(pinInput);
      setPinSaved(true);
      setPinInput("");
      setTimeout(() => setPinSaved(false), 3500);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio PIN");
    } finally { setPinSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-labor-muted">Il tuo profilo</div>
          <h1 className="font-display text-4xl">Ciao {user.first_name}</h1>
        </div>
        <button data-testid="user-logout" onClick={() => { logout(); nav("/"); }} className="border border-labor-border px-4 py-2 flex items-center gap-2 text-sm hover:bg-labor-ink hover:text-white">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>

      {/* CHATBOT PROMINENT BANNER */}
      <button
        data-testid="open-labor-ai-big"
        onClick={() => window.dispatchEvent(new Event("open-labor-ai"))}
        className="w-full mb-6 group text-left bg-gradient-to-r from-labor-terracotta to-labor-terracottaDark text-black p-5 flex items-center gap-4 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-labor-warning"
      >
        <div className="w-14 h-14 bg-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
          <Bot className="w-8 h-8 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl font-bold leading-tight text-black">APRI CHAT</div>
          <div className="text-sm text-black/85 mt-0.5">
            Chatta con Labor AI — le prime <strong>20 domande sono gratuite</strong>, poi 1-8 Labor Point per risposta.
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-sm font-bold uppercase tracking-widest whitespace-nowrap text-black">
          Apri <MessageSquare className="w-5 h-5" />
        </div>
      </button>

      <PushToggle />

      {/* NEWSLETTER CAF LABOR (aggiornata giornalmente da Vito/Marianna) */}
      {news.length > 0 && (
        <div className="paper-card p-5 mb-6" data-testid="user-news-feed">
          <div className="flex items-center gap-2 mb-3">
            <Megaphone className="w-5 h-5 text-labor-olive" />
            <h2 className="font-display text-xl">Newsletter CAF Labor</h2>
            <span className="text-xs text-labor-muted ml-auto">aggiornate ogni giorno</span>
          </div>
          <div className="space-y-2">
            {news.slice(0, 5).map((n) => (
              <div
                key={n.id}
                className={`border-l-4 p-3 text-sm ${n.pinned ? "border-labor-terracotta bg-labor-skyBg" : "border-labor-border bg-white"}`}
                data-testid={`news-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-labor-ink flex items-center gap-2">
                    {n.pinned && <span className="text-xs bg-labor-terracotta text-white px-1.5 py-0.5">IN EVIDENZA</span>}
                    {n.title}
                  </div>
                  <div className="text-xs text-labor-muted whitespace-nowrap">
                    {new Date(n.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                  </div>
                </div>
                {n.body && <div className="text-labor-muted mt-1 whitespace-pre-wrap">{n.body}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* WALLET SUMMARY */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="paper-card p-6 bg-gradient-to-br from-labor-olive to-labor-oliveLight text-white relative overflow-hidden" data-testid="card-free-questions">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90"><Gift className="w-4 h-4" /> Domande gratuite</div>
            <div className="font-display text-5xl mt-2" data-testid="free-questions">{user.free_questions ?? 0}</div>
            <div className="text-xs opacity-80 mt-1">rimaste dal bonus di benvenuto (20)</div>
          </div>
        </div>
        <div className="paper-card p-6 bg-gradient-to-br from-labor-terracotta to-labor-terracottaDark text-white relative overflow-hidden" data-testid="card-labor-points">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="relative">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90"><Coins className="w-4 h-4" /> Labor Point</div>
            <div className="font-display text-5xl mt-2" data-testid="labor-points">{user.labor_points ?? 0}</div>
            <div className="text-xs opacity-80 mt-1">1-8 punti per risposta (in base alla complessità)</div>
          </div>
        </div>
      </div>

      {/* TICKET URGENZA */}
      {tickets && (
        <div className="paper-card p-5 mb-6 border-l-4 border-l-labor-warning bg-yellow-50 flex items-start gap-3" data-testid="tickets-widget">
          <Ticket className="w-6 h-6 text-labor-warning flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-labor-ink">Ticket Urgenza · disponibili oggi: <span data-testid="tickets-remaining">{tickets.remaining}</span> / {tickets.total_available}</div>
            <div className="text-sm text-labor-muted mt-1">
              Hai un'urgenza e non trovi disponibilità per un appuntamento? In ufficio sono disponibili un numero limitato di ticket di urgenza cartacei. Vieni a trovarci in sede per verificare la disponibilità.
            </div>
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="flex gap-1 mb-6 border-b border-labor-border flex-wrap">
        {[
          ["wallet", "Ricarica Labor Point", ShoppingBag],
          ["bookings", "I miei appuntamenti", CalendarCheck],
          ["agenda", "Agenda Personale", NotebookPen],
          ["notifications", `Notifiche${unreadNotif > 0 ? ` (${unreadNotif})` : ""}`, Bell],
          ["docs", `Documenti${docs.length ? ` (${docs.length})` : ""}`, FileText],
          ["requests", "Richiedi documento", FilePlus],
          ["history", "Storico consumi", History],
        ].map(([k, l, Icon]) => (
          <button
            key={k}
            data-testid={`profilo-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-3 font-medium text-sm flex items-center gap-2 ${tab === k ? "border-b-2 border-labor-terracotta text-labor-ink" : "text-labor-muted"}`}
          >
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {/* WALLET TAB */}
      {tab === "wallet" && (
        <div>
          <div className="paper-card p-6 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-labor-terracotta" />
              <h2 className="font-display text-2xl">Ricarica Labor Point</h2>
            </div>
            <p className="text-labor-muted text-sm mb-5">
              Ogni risposta di Labor AI consuma da 1 a 8 Labor Point in base alla complessità della richiesta.
              Le prime 20 domande sono <strong>gratuite</strong>: dopo, serve una ricarica.
            </p>

            <div className="border-l-4 border-labor-olive bg-labor-skyBg p-5">
              <div className="font-display text-xl mb-2">Vuoi ricaricare i tuoi Labor Point?</div>
              <p className="text-sm text-labor-muted mb-4">
                Per ora puoi effettuare la ricarica <strong>direttamente presso la sede CAF Labor Bitonto</strong>.
              </p>
              <a
                data-testid="recharge-in-sede-btn"
                href="/contatti"
                className="olive-btn inline-flex items-center gap-2 px-5 py-2.5 font-semibold"
              >
                <Coins className="w-4 h-4" /> Ricarica in sede
              </a>
              <div className="mt-4 text-sm text-labor-ink space-y-1">
                <div><strong>CAF Labor di Vito Brilli</strong></div>
                <div>Via Zuavo n. 12 · 70032 Bitonto (BA)</div>
                <div>📞 <a href="tel:+393791466566" className="text-labor-olive hover:underline">379 146 6566</a></div>
                <div>✉ <a href="mailto:caflaborvitobitonto@gmail.com" className="text-labor-olive hover:underline">caflaborvitobitonto@gmail.com</a></div>
                <div className="text-xs text-labor-muted mt-2">Orari apertura: Lun/Mer 9:00-12:00 · Lun-Ven 16:00-18:30</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AGENDA TAB */}
      {tab === "agenda" && <AgendaPanel />}

      {/* BOOKINGS TAB */}
      {tab === "bookings" && (
        <div>
          <MyWaitlist />
          <div className="mb-6">
            <h3 className="font-display text-xl mb-3">Prossimi appuntamenti</h3>
            {bookings.upcoming.length === 0 ? (
              <div className="paper-card p-6 text-labor-muted text-sm">Non hai appuntamenti in programma. <Link to="/prenota" className="text-labor-olive font-semibold">Prenota ora →</Link></div>
            ) : (
              <div className="space-y-2">
                {bookings.upcoming.map((b) => (
                  <div key={b.id} className="paper-card p-4 grid sm:grid-cols-4 gap-3 items-center" data-testid={`upcoming-${b.id}`}>
                    <div className="flex items-center gap-2 text-labor-terracotta font-display text-lg"><Clock className="w-4 h-4" /> {b.slot_date} · {b.slot_time}</div>
                    <div className="text-sm sm:col-span-2"><strong>{b.service_name}</strong> con {b.staff_name}</div>
                    <div className="flex items-center gap-2 justify-self-end">
                      <div className="text-xs bg-labor-olive text-white px-2 py-1">{b.status.toUpperCase()}</div>
                      {b.status === "confermato" && (
                        <button data-testid={`cancel-booking-${b.id}`} onClick={() => cancelBooking(b)} className="text-xs text-red-600 border border-red-200 px-2 py-1 flex items-center gap-1 hover:bg-red-50"><X className="w-3 h-3" /> Annulla</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {bookings.past.length > 0 && (
            <div>
              <h3 className="font-display text-xl mb-3">Storico</h3>
              <div className="space-y-2">
                {bookings.past.slice(0, 10).map((b) => (
                  <div key={b.id} className="paper-card p-4 grid sm:grid-cols-4 gap-3 items-center opacity-70" data-testid={`past-${b.id}`}>
                    <div className="text-sm">{b.slot_date} · {b.slot_time}</div>
                    <div className="text-sm sm:col-span-2">{b.service_name}</div>
                    <div className="text-xs bg-labor-border text-labor-muted px-2 py-1 justify-self-end">{b.status.toUpperCase()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* NOTIFICATIONS TAB */}
      {tab === "notifications" && (
        <div>
          <div className="paper-card p-6 mb-4">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-labor-terracotta" />
                <h2 className="font-display text-2xl">Le mie notifiche</h2>
                {unreadNotif > 0 && (
                  <span data-testid="notif-unread-count" className="text-xs bg-labor-terracotta text-white px-2 py-0.5 font-bold">{unreadNotif} nuove</span>
                )}
              </div>
              {notifications.length > 0 && unreadNotif > 0 && (
                <button
                  data-testid="notif-read-all"
                  onClick={markAllNotifRead}
                  className="text-xs border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white px-3 py-1"
                >
                  Segna tutte come lette
                </button>
              )}
            </div>
            <p className="text-labor-muted text-sm mb-4">
              Qui trovi lo storico delle conferme di prenotazione e dei promemoria che ti arrivano il giorno prima dell'appuntamento.
            </p>
            {notifications.length === 0 ? (
              <div className="text-labor-muted text-sm border border-dashed border-labor-border p-6 text-center">
                Non hai ancora ricevuto notifiche. Prenotando un appuntamento riceverai subito una conferma e un promemoria il giorno prima.
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`notification-${n.id}`}
                    className={`border-l-4 p-3 flex items-start gap-3 ${!n.read ? "border-labor-terracotta bg-labor-skyBg" : "border-labor-border bg-white"}`}
                  >
                    <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center ${n.type === "booking_reminder" ? "bg-labor-warning text-white" : "bg-labor-italianGreen text-white"}`}>
                      {n.type === "booking_reminder" ? <Clock className="w-4 h-4" /> : <CalendarCheck className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="font-semibold text-sm">{n.title}</div>
                        <div className="text-xs text-labor-muted whitespace-nowrap">
                          {new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <div className="text-sm text-labor-muted mt-0.5">{n.body}</div>
                      <div className="flex gap-2 mt-2">
                        {!n.read && (
                          <button
                            data-testid={`notif-read-${n.id}`}
                            onClick={() => markNotifRead(n.id)}
                            className="text-xs text-labor-olive hover:underline"
                          >Segna come letta</button>
                        )}
                        <button
                          data-testid={`notif-delete-${n.id}`}
                          onClick={() => deleteNotif(n.id)}
                          className="text-xs text-red-600 hover:underline ml-auto"
                        >Elimina</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* DOCS TAB */}
      {tab === "docs" && (
        <div>
          <div className="paper-card p-6 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-labor-terracotta" />
              <h2 className="font-display text-2xl">Documenti condivisi dal CAF</h2>
            </div>
            <p className="text-labor-muted text-sm mb-4">
              Qui trovi i documenti che Vito o Marianna hanno preparato per te (ricevute, moduli, comunicazioni). I file vengono <strong>eliminati automaticamente dopo 3 giorni</strong> per motivi di privacy, quindi scaricali per tempo.
            </p>
            {docs.length === 0 ? (
              <div className="text-labor-muted text-sm border border-dashed border-labor-border p-6 text-center">
                Nessun documento condiviso al momento.
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 border border-labor-border p-3" data-testid={`user-doc-${d.id}`}>
                    <div className="flex-1 min-w-0 flex items-center gap-3">
                      <FileText className="w-5 h-5 text-labor-terracotta flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate" title={d.filename}>{d.filename}</div>
                        <div className="text-xs text-labor-muted">
                          {(d.size_bytes / 1024).toFixed(0)} KB · scade il {new Date(d.expires_at).toLocaleDateString("it-IT")}
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`user-doc-download-${d.id}`}
                      onClick={() => downloadUserDoc(d.id, d.filename)}
                      className="olive-btn px-4 py-2 text-sm font-semibold flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" /> Scarica
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* REQUESTS TAB */}
      {tab === "requests" && (
        <div>
          <div className="paper-card p-6 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FilePlus className="w-5 h-5 text-labor-terracotta" />
              <h2 className="font-display text-2xl">Richiedi un documento</h2>
            </div>
            <p className="text-labor-muted text-sm mb-4">
              Non hai più bisogno di scrivere su WhatsApp: mandaci qui la richiesta e Vito o Marianna te la
              prepareranno. Quando è pronta la troverai nella sezione <strong>"Documenti"</strong>.
            </p>
            <div className="grid gap-3">
              <label className="block text-sm">Tipo documento *
                <input
                  data-testid="req-doc-type"
                  type="text"
                  value={newDocReq.doc_type}
                  onChange={(e) => setNewDocReq({...newDocReq, doc_type: e.target.value})}
                  placeholder="Es. Certificato ISEE, 730, CIE, Successione…"
                  className="w-full mt-1 px-3 py-2 border border-labor-border"
                />
              </label>
              <label className="block text-sm">Descrizione della richiesta *
                <textarea
                  data-testid="req-doc-description"
                  value={newDocReq.description}
                  onChange={(e) => setNewDocReq({...newDocReq, description: e.target.value})}
                  placeholder="Spiega cosa ti serve: importo, periodo, destinatario…"
                  rows={4}
                  className="w-full mt-1 px-3 py-2 border border-labor-border"
                />
              </label>
              <label className="block text-sm">Note aggiuntive (opzionale)
                <input
                  data-testid="req-doc-notes"
                  type="text"
                  value={newDocReq.notes}
                  onChange={(e) => setNewDocReq({...newDocReq, notes: e.target.value})}
                  placeholder="Es. Ho urgenza entro venerdì"
                  className="w-full mt-1 px-3 py-2 border border-labor-border"
                />
              </label>
              <div className="text-sm">
                Allegato (opzionale · PDF o foto)
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <label className="border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white px-3 py-2 text-sm cursor-pointer flex items-center gap-2" data-testid="req-doc-attach-label">
                    <Paperclip className="w-4 h-4" /> {reqAttachment ? "Cambia file" : "Allega file"}
                    <input data-testid="req-doc-attach-input" type="file" accept={ACCEPTED_ATTACHMENTS} className="hidden"
                      onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; try { setReqAttachment(await readAttachment(f)); } catch (err) { alert(err.message); } }} />
                  </label>
                  {reqAttachment && (
                    <>
                      <AttachmentThumb mime={reqAttachment.mime} filename={reqAttachment.filename} previewUrl={reqAttachment.previewUrl} testId="req-doc-attach-thumb" />
                      <button data-testid="req-doc-attach-remove" onClick={() => setReqAttachment(null)} className="text-xs text-red-600 underline">Rimuovi</button>
                    </>
                  )}
                </div>
              </div>
              <button
                data-testid="req-doc-submit"
                onClick={submitDocRequest}
                disabled={reqSending}
                className="olive-btn px-5 py-2 font-semibold disabled:opacity-50 flex items-center gap-2 self-start"
              >
                {reqSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Invia richiesta
              </button>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-display text-xl mb-3">Le mie richieste ({docRequests.length})</h3>
            {docRequests.length === 0 ? (
              <div className="paper-card p-6 text-labor-muted text-sm">Non hai ancora inviato richieste.</div>
            ) : (
              <div className="space-y-2">
                {docRequests.map((r) => (
                  <div key={r.id} className="paper-card p-4 text-sm" data-testid={`doc-req-${r.id}`}>
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{r.doc_type}</div>
                        <div className="text-labor-muted text-xs mt-0.5">{new Date(r.created_at).toLocaleString("it-IT")}</div>
                      </div>
                      <span className={`text-xs px-2 py-1 font-semibold ${
                        r.status === "pronto" ? "bg-labor-italianGreen text-white" :
                        r.status === "in_lavorazione" ? "bg-labor-terracotta text-white" :
                        r.status === "annullato" ? "bg-red-600 text-white" :
                        "bg-labor-border text-labor-muted"
                      }`}>
                        {r.status === "in_lavorazione" ? "IN LAVORAZIONE" : r.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-2 text-labor-ink whitespace-pre-wrap">{r.description}</div>
                    {r.attachment_id && <div className="mt-2"><AttachmentThumb attId={r.attachment_id} mime={r.attachment_mime} filename={r.attachment_filename} testId={`doc-req-thumb-${r.id}`} /></div>}
                    {r.notes && <div className="mt-1 text-labor-muted text-xs italic">Note: {r.notes}</div>}
                    {r.admin_note && (
                      <div className="mt-2 border-l-2 border-labor-olive pl-2 text-xs bg-labor-skyBg p-2">
                        <strong>Risposta CAF:</strong> {r.admin_note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === "history" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-display text-xl mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Consumi Labor AI</h3>
            <div className="paper-card p-6 text-center" data-testid="consumptions-total-card">
              <div className="text-xs uppercase tracking-widest text-labor-muted">Domande fatte a Labor AI</div>
              <div className="font-display text-6xl mt-2 text-labor-terracotta" data-testid="consumptions-total">
                {history.consumptions.length}
              </div>
              <div className="text-xs text-labor-muted mt-2">
                {history.consumptions.filter((c) => c.subtype === "free").length} gratis · {history.consumptions.filter((c) => c.subtype !== "free").length} pagate
              </div>
              <button
                data-testid="open-labor-ai-history"
                onClick={() => window.dispatchEvent(new Event("open-labor-ai"))}
                className="olive-btn mt-4 px-5 py-2 font-bold text-sm inline-flex items-center gap-2"
              >
                <Bot className="w-4 h-4" /> Apri Labor AI
              </button>
            </div>
          </div>
          <div>
            <h3 className="font-display text-xl mb-3 flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Acquisti</h3>
            {history.purchases.length === 0 ? (
              <div className="paper-card p-5 text-labor-muted text-sm">Nessun acquisto ancora.</div>
            ) : (
              <div className="space-y-2">
                {history.purchases.map((p) => (
                  <div key={p.id} className="paper-card p-4" data-testid={`purchase-${p.id}`}>
                    <div className="flex justify-between items-center">
                      <div className="font-medium">{p.package_name}</div>
                      <div className="font-display text-lg text-labor-olive">€{p.amount_eur?.toFixed(2)}</div>
                    </div>
                    <div className="text-xs text-labor-muted mt-1">+{p.labor_points} Labor Point · {new Date(p.created_at).toLocaleString("it-IT")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* USER INFO */}
      <div className="paper-card p-5 mt-8 bg-labor-skyBg">
        <div className="flex items-center gap-2 text-labor-muted text-sm">
          <User className="w-4 h-4" /> {user.email} {user.phone && `· ${user.phone}`} {user.auth_provider === "google" && <span className="bg-white border border-labor-border text-xs px-2 py-0.5">GOOGLE</span>}
        </div>
        <div className="mt-2 text-xs">
          <Link to="/privacy-policy" data-testid="profile-privacy-link" className="text-labor-olive hover:underline">Privacy Policy</Link>
          <span className="text-labor-muted"> · </span>
          <Link to="/termini-di-servizio" data-testid="profile-terms-link" className="text-labor-olive hover:underline">Termini di Servizio</Link>
          <span className="text-labor-muted"> · I tuoi dati sono trattati nel rispetto del GDPR</span>
        </div>
      </div>

      {/* PIN RAPIDO */}
      <div className="paper-card p-5 mt-4" data-testid="pin-section">
        <div className="flex items-center gap-2 mb-2">
          <Key className="w-5 h-5 text-labor-olive" />
          <h3 className="font-display text-xl">PIN rapido (accesso mobile veloce)</h3>
        </div>
        <p className="text-sm text-labor-muted mb-4">
          Imposta un PIN di 4-6 cifre per accedere velocemente da cellulare senza dover scrivere ogni volta la password (come nell'app INPS).
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            data-testid="pin-input"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="PIN (4-6 cifre)"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ""))}
            className="px-4 py-3 border border-labor-border text-center font-display text-2xl tracking-widest w-48"
          />
          <button
            data-testid="pin-save"
            onClick={handleSavePin}
            disabled={pinSaving || pinInput.length < 4}
            className="olive-btn px-5 py-3 font-semibold disabled:opacity-40 flex items-center gap-2"
          >
            {pinSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
            Imposta PIN
          </button>
          {pinSaved && <span className="text-labor-italianGreen text-sm font-semibold">✓ PIN salvato</span>}
        </div>
      </div>

      {/* FEEDBACK */}
      <div className="mt-6">
        <FeedbackWidget />
      </div>

      {/* CAMBIO PASSWORD */}
      {user.auth_provider !== "google" && (
        <div className="paper-card p-5 mt-6" data-testid="change-password-section">
          <h3 className="font-display text-xl mb-2 flex items-center gap-2 text-labor-ink">
            <Lock className="w-5 h-5 text-labor-olive" /> Cambia password
          </h3>
          <p className="text-xs text-labor-muted mb-3">
            Requisiti: min 8 caratteri, almeno 1 numero e 1 lettera maiuscola.
          </p>
          <div className="grid sm:grid-cols-3 gap-2">
            <input
              data-testid="pw-old"
              type="password"
              placeholder="Password attuale"
              autoComplete="current-password"
              value={pwForm.old_password}
              onChange={(e) => setPwForm({...pwForm, old_password: e.target.value})}
              className="px-3 py-2 border border-labor-border text-sm"
            />
            <input
              data-testid="pw-new"
              type="password"
              placeholder="Nuova password"
              autoComplete="new-password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm({...pwForm, new_password: e.target.value})}
              className="px-3 py-2 border border-labor-border text-sm"
            />
            <input
              data-testid="pw-new2"
              type="password"
              placeholder="Ripeti nuova password"
              autoComplete="new-password"
              value={pwForm.new_password2}
              onChange={(e) => setPwForm({...pwForm, new_password2: e.target.value})}
              className="px-3 py-2 border border-labor-border text-sm"
            />
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <button
              data-testid="pw-submit"
              onClick={handleChangePassword}
              disabled={pwSaving}
              className="olive-btn px-4 py-2 text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Aggiorna password
            </button>
            {pwMsg && (
              <span data-testid="pw-msg" className={`text-sm ${pwMsg.type === "ok" ? "text-labor-italianGreen" : "text-red-600"}`}>
                {pwMsg.text}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ZONA PERICOLO — ELIMINA ACCOUNT */}
      <div className="paper-card p-5 mt-6 border-l-4 border-red-500" data-testid="danger-zone">
        <h3 className="font-display text-xl mb-2 text-red-700 flex items-center gap-2">
          <Trash2 className="w-5 h-5" /> Elimina il mio account
        </h3>
        <p className="text-sm text-labor-muted mb-3 leading-relaxed">
          Ai sensi dell'art. 17 GDPR ("diritto all'oblio") puoi cancellare in qualsiasi momento il tuo account.
          L'operazione è <strong>irreversibile</strong>: i tuoi dati anagrafici, chat, documenti condivisi e
          appuntamenti futuri saranno rimossi. Restano solo i dati contabili anonimizzati previsti per legge.
        </p>
        <button
          data-testid="delete-account-btn"
          onClick={handleDeleteAccount}
          disabled={deleting}
          className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {deleting ? "Eliminazione in corso…" : "Elimina definitivamente il mio account"}
        </button>
      </div>
    </div>
  );
}
